import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Filter, X } from "lucide-react";
import axios from "axios";
import { ProductCard } from "../components/ProductCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FelpePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);

  const activeCollection = searchParams.get("collection");

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API}/products?category=felpe`;
        if (activeCollection) {
          url += `&collection=${activeCollection}`;
        }
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [activeCollection]);

  const collections = ["astro", "drinks", "quotes"];

  const toggleCollection = (collection) => {
    if (activeCollection === collection) {
      searchParams.delete("collection");
    } else {
      searchParams.set("collection", collection);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="felpe-page">
      {/* Header */}
      <section className="bg-accent-pink text-white py-16 border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-6xl sm:text-7xl mb-4">FELPE</h1>
            <p className="font-body text-xl text-pink-100 max-w-xl">
              Felpe con ricami unici. Comfort, stile e qualità.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters Bar */}
      <section className="sticky top-20 z-40 bg-white border-b-4 border-black py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden btn-brutal-outline text-sm flex items-center"
              >
                <Filter size={16} className="mr-2" />
                Filtri
              </button>

              {/* Desktop Filters */}
              <div className="hidden md:flex items-center gap-3">
                <span className="font-mono text-sm uppercase text-gray-500">Collezione:</span>
                {collections.map((collection) => (
                  <button
                    key={collection}
                    onClick={() => toggleCollection(collection)}
                    className={`
                      font-mono text-sm uppercase px-4 py-2 border-2 border-black
                      transition-all duration-200
                      ${activeCollection === collection 
                        ? collection === 'astro' ? 'bg-accent-blue shadow-brutal' 
                          : collection === 'drinks' ? 'bg-accent-pink text-white shadow-brutal'
                          : 'bg-accent-yellow shadow-brutal'
                        : 'bg-white hover:bg-gray-100'
                      }
                    `}
                    data-testid={`filter-${collection}`}
                  >
                    {collection}
                  </button>
                ))}
                {activeCollection && (
                  <button
                    onClick={() => {
                      searchParams.delete("collection");
                      setSearchParams(searchParams);
                    }}
                    className="p-2 border-2 border-black hover:bg-red-100"
                    data-testid="clear-filters"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            <span className="font-mono text-sm text-gray-500">
              {products.length} prodotti
            </span>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="md:hidden mt-4 flex flex-wrap gap-2">
              {collections.map((collection) => (
                <button
                  key={collection}
                  onClick={() => toggleCollection(collection)}
                  className={`
                    font-mono text-sm uppercase px-4 py-2 border-2 border-black
                    ${activeCollection === collection 
                      ? collection === 'astro' ? 'bg-accent-blue' 
                        : collection === 'drinks' ? 'bg-accent-pink text-white'
                        : 'bg-accent-yellow'
                      : 'bg-white'
                    }
                  `}
                >
                  {collection}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="border-2 border-black p-4 animate-pulse">
                  <div className="aspect-square bg-gray-200 mb-4"></div>
                  <div className="h-6 bg-gray-200 mb-2"></div>
                  <div className="h-4 bg-gray-200 w-2/3"></div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-heading text-3xl mb-4">NESSUN PRODOTTO</p>
              <p className="font-body text-gray-600">
                Nessun prodotto trovato con i filtri selezionati.
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
